import { useEffect } from 'react';
import customerService from '../../../services/customerService';
import { PAGINATION_CONFIG, SORT_CONFIG } from '../utils/customerConstants';

export const useCustomerData = (
  userProfile,
  debouncedSearchTerm,
  currentPage,
  setCustomers,
  setCustomerCount,
  setPagination,
  setIsLoading,
  showNotification,
  sortBy,
  techFilter
) => {
  // Get franchisee ID for current context (may be overridden by technician filter)
  const getFranchiseeId = () => {
    if (techFilter?.showFilter) {
      return techFilter.getFilteredFranchiseeId();
    }
    if (userProfile?.role === 'admin') {
      return 'all';
    } else if (userProfile?.role === 'franchisee') {
      return userProfile.id;
    } else if (userProfile?.role === 'technician' && (userProfile?.franchiseeId || userProfile?.assignedFranchiseeId)) {
      return userProfile.franchiseeId || userProfile.assignedFranchiseeId;
    }
    return null;
  };

  // Load customers with server-side pagination and search
  // Note: customers are franchisee-scoped, not branch-scoped (branch_id is always NULL)
  const loadCustomers = async (page = 1, search = '') => {
    const franchiseeId = getFranchiseeId();
    if (!franchiseeId) {
      setCustomers([]);
      setCustomerCount(0);
      setPagination({
        page: 1,
        limit: PAGINATION_CONFIG.CUSTOMERS_PER_PAGE,
        total: 0,
        pages: 0
      });
      return;
    }

    setIsLoading(true);
    try {
      const params = {
        page,
        limit: PAGINATION_CONFIG.CUSTOMERS_PER_PAGE,
        search: search || undefined,
        sortBy: sortBy || SORT_CONFIG.DEFAULT_SORT_BY,
        sortOrder: sortBy === 'name' ? 'ASC' : SORT_CONFIG.DEFAULT_SORT_ORDER
      };

      const response = await customerService.getCustomers(franchiseeId, params);

      if (response.customers) {
        setCustomers(response.customers);
        setPagination(response.pagination || { 
          page, 
          limit: PAGINATION_CONFIG.CUSTOMERS_PER_PAGE, 
          total: 0, 
          pages: 0 
        });
        
        // Get total count for delete button and display
        if (response.pagination?.total !== undefined) {
          setCustomerCount(response.pagination.total);
        } else {
          try {
            const count = await customerService.getCustomerCount(franchiseeId);
            setCustomerCount(count);
          } catch (error) {
            console.error('Failed to load customer count:', error);
            setCustomerCount(response.customers.length);
          }
        }
      }
    } catch (error) {
      console.error('Error loading customers:', error);
      showNotification('Failed to load customers', 'error');
      setCustomers([]);
      setCustomerCount(0);
    } finally {
      setIsLoading(false);
    }
  };

  // Load initial data when userProfile changes
  useEffect(() => {
    loadCustomers(1, debouncedSearchTerm);
  }, [userProfile]);

  // Load customers when search term, sort, or technician filter changes
  useEffect(() => {
    loadCustomers(1, debouncedSearchTerm);
  }, [debouncedSearchTerm, sortBy, techFilter?.selectedTechnician]);

  // Load customers when page changes
  useEffect(() => {
    if (currentPage > 1) { // Only reload if not already on page 1
      loadCustomers(currentPage, debouncedSearchTerm);
    }
  }, [currentPage]);

  return {
    getFranchiseeId,
    loadCustomers
  };
};